import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiTrendingUp, FiTarget, FiArrowRight, FiActivity } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

export const ROICalculatorPage: React.FC = () => {
  useDocumentTitle(
    'Marketing ROI Calculator | Calculate Your Advertising Returns',
    'Calculate your marketing ROI before you invest. Understand break-even metrics, projected revenue, and scaling strategies for Google Ads, Meta Ads, and more.'
  );

  const [inputs, setInputs] = useState({
    adSpend: '',
    avgOrderValue: '',
    conversionRate: '',
    profitMargin: ''
  });

  const [results, setResults] = useState({
    breakEvenROAS: 0,
    projectedRevenue: 0,
    projectedProfit: 0,
    recommendedBudget: 0
  });

  const calculateROI = () => {
    const spend = parseFloat(inputs.adSpend) || 0;
    const aov = parseFloat(inputs.avgOrderValue) || 0;
    const cr = parseFloat(inputs.conversionRate) / 100 || 0;
    const margin = parseFloat(inputs.profitMargin) / 100 || 0;

    const breakEvenROAS = 1 / margin;
    const conversions = (spend * cr) / 100;
    const revenue = conversions * aov;
    const profit = revenue * margin - spend;
    const recommended = profit > 0 ? spend * 1.5 : spend * 0.8;

    setResults({
      breakEvenROAS: breakEvenROAS,
      projectedRevenue: revenue,
      projectedProfit: profit,
      recommendedBudget: recommended
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <FiActivity className="text-6xl text-white mx-auto mb-6" />
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Calculate Your Marketing ROI Before You Invest
            </h1>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Make data-driven decisions with our free ROI calculator
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why ROI Calculation */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Why ROI Calculation Is Important</h2>
            <p className="text-lg text-gray-600">
              Many businesses invest in ads without understanding break-even metrics. This leads to overspending and frustration.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Input Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Enter Your Data</h3>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Monthly Ad Spend (₹)
                  </label>
                  <input
                    type="number"
                    name="adSpend"
                    value={inputs.adSpend}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="50000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Average Order Value (₹)
                  </label>
                  <input
                    type="number"
                   name="avgOrderValue"
                    value={inputs.avgOrderValue}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="2000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Conversion Rate (%)
                  </label>
                  <input
                    type="number"
                    name="conversionRate"
                    value={inputs.conversionRate}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="3"
                    step="0.1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Target Profit Margin (%)
                  </label>
                  <input
                    type="number"
                    name="profitMargin"
                    value={inputs.profitMargin}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="40"
                    step="0.1"
                  />
                </div>

                <button
                  onClick={calculateROI}
                  className="w-full px-6 py-4 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all flex items-center justify-center gap-2"
                >
                  <FiActivity />
                  Calculate ROI
                </button>
              </div>
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 shadow-xl"
            >
              <h3 className="text-2xl font-bold text-gray-800 mb-6">Your Results</h3>
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <FiTarget className="text-2xl text-green-600" />
                    <h4 className="font-bold text-gray-700">Break-Even ROAS</h4>
                  </div>
                  <p className="text-3xl font-bold text-green-600">
                    {results.breakEvenROAS.toFixed(2)}x
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <FiDollarSign className="text-2xl text-blue-600" />
                    <h4 className="font-bold text-gray-700">Projected Revenue</h4>
                  </div>
                  <p className="text-3xl font-bold text-blue-600">
                    ₹{results.projectedRevenue.toLocaleString('en-IN')}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <FiTrendingUp className="text-2xl text-purple-600" />
                    <h4 className="font-bold text-gray-700">Projected Profit</h4>
                  </div>
                  <p className={`text-3xl font-bold ${results.projectedProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    ₹{results.projectedProfit.toLocaleString('en-IN')}
                  </p>
                </div>

                <div className="bg-white rounded-xl p-6">
                  <h4 className="font-bold text-gray-700 mb-2">Recommended Budget</h4>
                  <p className="text-2xl font-bold text-gray-800">
                    ₹{results.recommendedBudget.toLocaleString('en-IN')}/month
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* When to Scale */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-gray-800 mb-8">When to Increase Budget</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: 'Stable CPA', desc: 'Cost per acquisition remains consistent over 30 days' },
                { title: 'Positive ROAS', desc: 'Return on ad spend exceeds break-even point' },
                { title: 'Consistent Conversion Rate', desc: 'CR stays above 2% for your industry' }
              ].map((item, index) => (
                <div key={index} className="bg-gray-50 rounded-xl p-6 border-2 border-green-200">
                  <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-green-600 to-emerald-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Help Optimizing Your ROI?</h2>
          <p className="text-white/90 mb-8">Book a strategy consultation with our experts</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-700 rounded-lg font-bold hover:bg-gray-100 transition-all"
          >
            Book ROI Strategy Consultation
            <FiArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ROICalculatorPage;
